import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { PRODUCT_UNITS, PRODUCT_TENORS, ProductFormState, validateProductForm, parseTenors } from '@/lib/product-schema';

interface Props {
  form: ProductFormState;
  setForm: React.Dispatch<React.SetStateAction<ProductFormState>>;
  categories: string[];
  isEditing?: boolean;
  showErrors?: boolean;
  /** Asl narx maydonini ko'rsatish (do'kon mahsulotlari uchun) */
  showOriginalPrice?: boolean;
}

export default function ProductFormFields({
  form, setForm, categories, isEditing, showErrors, showOriginalPrice,
}: Props) {
  const { t } = useTranslation();
  const errors = showErrors ? validateProductForm(form) : {};
  const err = (key: string) => errors[key];

  const field = (key: keyof ProductFormState, label: string, extra?: { maxLength?: number; type?: string }) => (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label} *</label>
      <Input
        placeholder={label}
        value={String(form[key] ?? '')}
        maxLength={extra?.maxLength}
        type={extra?.type}
        disabled={key === 'sku' && isEditing}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
        className={err(key as string) ? 'border-destructive' : ''}
      />
      {err(key as string) && <p className="text-xs text-destructive">{err(key as string)}</p>}
    </div>
  );

  const allCats = [...new Set([...categories, ...(form.category && !categories.includes(form.category) ? [form.category] : [])])];

  const selectField = (key: 'category' | 'unit', label: string, options: { value: string; label: string }[]) => (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-foreground">{label} *</label>
      <Select value={form[key] || undefined} onValueChange={v => setForm(f => ({ ...f, [key]: v }))}>
        <SelectTrigger className={err(key) ? 'border-destructive' : ''}>
          <SelectValue placeholder={t('cat_products.selectPlaceholder', { label })} />
        </SelectTrigger>
        <SelectContent>
          {options.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
      {err(key) && <p className="text-xs text-destructive">{err(key)}</p>}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">{field('name', t('cat_common.name'))}</div>
        <div className="md:col-span-2">{field('sku', 'SKU')}</div>
        {field('brend_nomi', t('cat_products.brandName'))}
        {field('atribut_nomi', t('cat_common.attribute'))}
        {field('mhik', t('cat_products.mxikCode'), { maxLength: 17 })}
        {field('mxik_nomi', t('cat_products.mxikName'))}
        {field('barcode', t('cat_products.barcode'))}
        {selectField('category', t('cat_common.category'), allCats.map(c => ({ value: c, label: c })))}
        {selectField('unit', t('cat_common.unit'), PRODUCT_UNITS.map(u => ({ value: u, label: u })))}
        {showOriginalPrice && (
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('cat_products.originalPrice')}</label>
            <Input placeholder={t('cat_products.originalPrice')} type="number" value={form.price}
              onChange={e => setForm(f => ({ ...f, price: e.target.value }))} />
          </div>
        )}
        {field('sale_price', t('cat_products.price'), { type: 'number' })}
      </div>

      <div className="space-y-2 pt-2">
        <label className="text-sm font-medium text-foreground">
          {t('cat_products.installmentTerms')}{form.is_murabaha_eligible ? ' *' : ''}
        </label>
        <div className="flex flex-wrap gap-3">
          {PRODUCT_TENORS.map(tenor => {
            const selected = parseTenors(form.allowed_tenors).includes(tenor);
            return (
              <label
                key={tenor}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition-colors ${
                  selected ? 'border-primary bg-primary/5 text-foreground' : 'border-border text-muted-foreground'
                }`}
              >
                <Checkbox
                  checked={selected}
                  onCheckedChange={v => setForm(f => {
                    const current = parseTenors(f.allowed_tenors);
                    const next = v ? [...current, tenor] : current.filter(x => x !== tenor);
                    return { ...f, allowed_tenors: parseTenors(next) };
                  })}
                />
                {t('cat_products.monthsN', { n: tenor })}
              </label>
            );
          })}
        </div>
        {err('allowed_tenors') && <p className="text-xs text-destructive">{err('allowed_tenors')}</p>}
      </div>

      <div className="flex items-center gap-2 pt-2">
        <Checkbox id="murabaha-eligible" checked={form.is_murabaha_eligible}
          onCheckedChange={v => setForm(f => ({ ...f, is_murabaha_eligible: !!v }))} />
        <label htmlFor="murabaha-eligible" className="text-sm text-foreground">{t('cat_products.murabahaEligible')}</label>
      </div>
    </div>
  );
}
